// discoveryApi.ts
// ===============
//
// Wave 14ca-fix58: API-Client für Admin Discovery Mode.
// - snapPosition: GPS → nächste OSM-way-id
// - markWayOverride: einzelner Way als blocked/approved/one_way_X markieren
// - batchMarkApproved: Liste von Positions auf Server snappen + alle als
//   approved persistieren (für Auto-Approve nach Ride-Stop)
//
// Auth: alle /api/admin/way-overrides-Endpoints brauchen einen eingeloggten
// Admin / admin_light. authFetch reicht.

import { authFetch } from './auth';

export type WayOverrideStatus =
  | 'approved'
  | 'blocked'
  | 'one_way_forward'
  | 'one_way_reverse';

export interface SnapResult {
  edgeId: number;
  wayId: string | null;
  highway: string | null;
  source: string | null;
  distM: number;
  fromNodeId: number;
  toNodeId: number;
  fromLat: number;
  fromLon: number;
  toLat: number;
  toLon: number;
  /** Wave 14ca-fix64: Total-Length aller Edges mit derselben way_id, in m.
   *  Nur gesetzt wenn wayId nicht null — fuer das WAY-ID-Pick-Mode-UI. */
  wayLengthM?: number | null;
  /** OSM `name`-Tag des Ways, falls vorhanden. */
  wayName?: string | null;
  /** Wave 14ca-fix64: Way-Geometrie zum Highlighten auf der Karte.
   *  Pro Edge ein Segment ([from, to] mit [lat, lon]). center + bbox
   *  fuer optionales fitBounds / Zentrierung. Null wenn navi.sqlite
   *  die way_id nicht aufloesen kann. */
  wayGeometry?: {
    center: [number, number];
    bbox: [number, number, number, number];
    segments: Array<Array<[number, number]>>;
  } | null;
}

/**
 * Snap GPS-Position auf naechste OSM-Way (radius default 30m).
 *
 * Wave 14cf: optional `region` durchreichen, damit das Backend die richtige
 * navi-{region}.sqlite trifft. Default 'liguria' wenn nicht gesetzt.
 */
export async function snapPosition(
  lat: number,
  lon: number,
  radiusM = 30,
  region?: 'liguria' | 'elba',
): Promise<SnapResult | null> {
  try {
    const res = await fetch('/api/navi/snap-position', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lat, lon, radiusM, region }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { snap?: SnapResult | null };
    return body.snap ?? null;
  } catch (err) {
    console.warn('[discovery] snap failed', err);
    return null;
  }
}

export interface MarkOverrideInput {
  osmWayId: string;
  status: WayOverrideStatus;
  notes?: string;
  source?: 'manual' | 'auto';
}

/** Single Override setzen (Admin-Click waehrend Ride). */
export async function markWayOverride(
  input: MarkOverrideInput,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await authFetch('/api/admin/way-overrides', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...input,
        source: input.source ?? 'manual',
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { ok: false, error: `http_${res.status}: ${text}` };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'network',
    };
  }
}

// ─── Crux-Spots (Wave 14ca-fix64) ──────────────────────────────────────
// Free-floating Schluesselstellen-Marker — punkt-basiert, trail-unabhaengig.
// Auth: admin_light reicht (im Gegensatz zu way_overrides die admin_light_plus
// brauchen). User-Wunsch: Schluesselstellen-Button auch fuer admin_light.

export interface MarkCruxSpotInput {
  lat: number;
  lon: number;
  /** Optional: Hoehe in m (aus GPS-altitude oder SRTM). */
  ele?: number | null;
  /** Optional: kurzer Freitext, max 500 Zeichen serverseitig getrimmt. */
  note?: string | null;
}

/** Schluesselstelle (punkt-basiert) am aktuellen GPS-Fix anlegen. */
export async function markCruxSpot(
  input: MarkCruxSpotInput,
): Promise<{ ok: boolean; id?: string; error?: string }> {
  try {
    const res = await authFetch('/api/admin/crux-spots', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lat: input.lat,
        lon: input.lon,
        ele: input.ele ?? null,
        note: input.note ?? null,
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { ok: false, error: `http_${res.status}: ${text}` };
    }
    const body = await res.json().catch(() => ({}) as { id?: string });
    return { ok: true, id: typeof body?.id === 'string' ? body.id : undefined };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'network',
    };
  }
}

/** Auto-Approve: Liste von GPS-Positions zum Backend, dort werden sie
    gesnapped und alle einzigartigen way-ids als approved persistiert.
    Frontend macht das nach Ride-Stop in einem Batch.
    Returns { applied, skipped } oder null bei Fehler. */
export async function batchMarkApprovedFromPositions(
  positions: Array<{ lat: number; lon: number }>,
): Promise<{ applied: number; skipped: number } | null> {
  if (positions.length === 0) return { applied: 0, skipped: 0 };
  // Schritt 1: alle positions snappen, way-ids sammeln (deduped)
  // Wir machen das parallel, aber sequenziell genug fuer rate-limiting.
  // Alternative waere ein backend-batch-snap-Endpoint; fuer MVP reicht das.
  const wayIds = new Set<string>();
  // Sample alle ~50m oder ~10s — Frontend hat schon vorgefiltert beim
  // GPS-Append (siehe rideRecorder appendPoint). Trotzdem hier nur jeden
  // 5. Punkt snappen damit's nicht zu viele Requests werden.
  const STRIDE = Math.max(1, Math.floor(positions.length / 200));
  const sampled = positions.filter((_, i) => i % STRIDE === 0);
  const results = await Promise.all(
    sampled.map((p) => snapPosition(p.lat, p.lon, 25)),
  );
  for (const snap of results) {
    if (snap?.wayId) wayIds.add(snap.wayId);
  }
  if (wayIds.size === 0) return { applied: 0, skipped: 0 };

  // Schritt 2: batch upsert
  try {
    const res = await authFetch('/api/admin/way-overrides/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: 'auto',
        items: [...wayIds].map((id) => ({
          osmWayId: id,
          status: 'approved' as WayOverrideStatus,
        })),
      }),
    });
    if (!res.ok) return null;
    return (await res.json()) as { applied: number; skipped: number };
  } catch (err) {
    console.warn('[discovery] batch failed', err);
    return null;
  }
}
